import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AuthUser } from '@app/account/account-api';
import { ProfileOverviewService } from '@app/profile/components/profile-overview/profile-overview.service';
import {
  CollectionCounts,
  TagSignalHistory,
  SignalSummary,
  TagSignalCount,
  SignalSummaryMap,
} from '@app/profile/profile-api';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';
import { EngagedLearnerService } from '@app/shared/services/engaged-learner.service';
import { TagsService } from '@app/tags/services/tags.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TrackingEventArgs } from '@app/shared/services/tracking';
import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import { WindowToken } from '@app/shared/window.token';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, ObservableInput, of, Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { FollowingService } from '@app/shared/services/following.service';
import { InputsService } from '@app/inputs/services/inputs.service';
import { UserStaticSettingsService } from '@app/profile-settings/user-static-settings.service';
import { Profile } from '@app/profile/profile';
import { TagsApi } from '@app/tags/tag-api';
import { ProfileService } from '@app/profile/services/profile.service';
import { Router } from '@angular/router';

interface LoadingStates {
  page: boolean;
  learnings: boolean;
  experiences?: boolean;
  achievements?: boolean;
}

@Component({
  selector: 'dgx-profile-overview',
  templateUrl: './profile-overview.component.html',
  styleUrls: ['./profile-overview.component.scss'],
})
export class ProfileOverviewComponent
  extends SubscriberBaseDirective
  implements OnInit, OnDestroy
{
  public readonly CLASS_NAME = 'ProfileOverviewComponent';

  public i18n = this.translate.instant([
    'Core_Empty_Image_Alt',
    'dgProfileOverview_OverviewEmptyStateTitle',
    'dgProfileOverview_OverviewEmptyStateText',
    'dgProfileOverview_OverviewCompleteWizardButton',
    'dgProfileOverview_OverviewSkillsTitle',
    'dgProfileOverview_OverviewExperienceTitle',
    'dgProfileOverview_OverviewAchievementsTitle',
    'dgProfileOverview_OverviewLearningTitle',
    'dgProfileOverview_OverviewNotProvidedTitle',
    'dgProfileOverview_NonOwnerEmptyStateTitle',
  ]);

  @Input() public owner: Profile;
  @Input() public isEngaged: boolean;
  @Input() public privacyId: number;

  // Local
  public isLoading: boolean = true;
  public hasNoSkills: boolean = false;
  public isLoggedIn: boolean;
  public hasParsedResume: boolean;

  public xAxisLabels: string[];
  public signalHistories: TagSignalHistory[];
  public signalCounts: TagSignalCount[];
  public maxSkills: number = 6;
  public signalSummaries: SignalSummaryMap;

  public skills: TagsApi.FocusableTagDetails = { focus: [], other: [] };

  public collectionCounts: CollectionCounts = {
    learningsCount: 0,
    achievementsCount: 0,
    experiencesCount: 0,
  };
  public skillRatingsCount: number = 0;
  public authUser: AuthUser;
  public loadingStates: LoadingStates = {
    page: true,
    learnings: true,
  };

  public showExperiences: boolean;
  public showAchievements: boolean;

  public ownerKey: number;

  private events: Subscription[] = [];

  public get ownerIsViewing() {
    return this.profileService.ownerIsViewing;
  }

  constructor(
    private tagsService: TagsService,
    private translate: TranslateService,
    private tracker: TrackerService,
    private webEnvironmentService: WebEnvironmentService,
    private profileOverviewService: ProfileOverviewService,
    private authService: AuthService,
    private inputsService: InputsService,
    private engagedLearnerService: EngagedLearnerService,
    @Inject(WindowToken) private windowRef: Window,
    private userStaticSettingsService: UserStaticSettingsService,
    private followingService: FollowingService,
    private profileService: ProfileService,
    private router: Router
  ) {
    super();
  }

  public get emptyImageUrl(): string {
    return this.webEnvironmentService.getBlobUrl(
      '/content/img/emptystate/profile-overview-empty-state.svg',
      true
    );
  }

  public get nonOwnerEmptyStateText(): string {
    return this.translate.instant('dgProfileOverview_NonOwnerEmptyStateBody', {
      firstName: this.owner.name.split(' ')[0],
    });
  }

  public get nonOwnerEmptyImageUrl(): string {
    return this.webEnvironmentService.getBlobUrl(
      '/content/img/emptystate/profile-settings.svg',
      true
    );
  }

  public ngOnInit(): void {
    // Update skill counts when user tags are updated
    this.events.push(
      this.tagsService.userTagsModified.subscribe(() => {
        this.updateSkillsCounts();
      }),
      this.inputsService.inputModify.subscribe((...args) => {
        this.updateSkillsCounts();
      })
    );

    this.authUser = this.authService?.authUser;

    this.showExperiences = !this.authUser?.hideOverviewExperience;
    this.showAchievements = !this.authUser?.hideOverviewAchievement;
    this.setupLoadingStates();

    this.isLoggedIn = !!this.authUser;
    this.ownerKey = this.owner.id;

    forkJoin({
      hasParsedResume: this.ownerIsViewing
        ? this.profileOverviewService.getHasParsedResume()
        : of({ hasBeenParsed: false }),
      profileSettings: this.getPersonalSettings(),
      collectionCounts: this.profileOverviewService.getCollectionCounts(
        this.ownerKey,
        this.isLoggedIn
      ),
      skillsAndRatingsCount: this.profileOverviewService.getUserSkills(
        this.ownerKey
      ),
      signalCounts: this.profileOverviewService.getSignalCounts(this.ownerKey),
    })
      .pipe(
        take(1),
        switchMap(
          ({
            hasParsedResume,
            profileSettings,
            collectionCounts,
            skillsAndRatingsCount,
            signalCounts,
          }): ObservableInput<any> => {
            this.hasParsedResume = hasParsedResume.hasBeenParsed;
            this.profileOverviewService.setLocaleId(
              // tslint:disable-next-line: no-string-literal
              profileSettings['LocaleId']?.lcid
            );
            this.collectionCounts = collectionCounts;
            const { skills, ratingsTotalCount } = skillsAndRatingsCount;
            this.skillRatingsCount = ratingsTotalCount;
            this.setSkills(skills);
            this.signalCounts = signalCounts.signalCounts;
            this.signalHistories = signalCounts.signalHistories;
            this.xAxisLabels = signalCounts.xAxisLabels;

            if (this.hasSkills(skills)) {
              const focusTagIds = skills.focus.map((s) => s.tagId);
              const otherTagIds = skills.other
                .slice(0, 6)
                .reduce((allSkills, skill) => {
                  if (!focusTagIds.includes(skill.tagId)) {
                    allSkills.push(skill.tagId);
                  }
                  return allSkills;
                }, []);

              const allTagIds = [...focusTagIds, ...otherTagIds].join(',');

              return this.profileOverviewService.getSignalBreakdown(
                this.ownerKey,
                allTagIds
              );
            }
            this.updateLoadingState({ type: 'page', status: false });
            return of([]);
          }
        )
      )
      .subscribe((signalSummary) => {
        if (signalSummary) {
          this.signalSummaries = signalSummary.reduce((all, current) => {
            all[current.tagId] = current;
            return all;
          }, {});
        }
        this.trackProfileView();
        this.updateLoadingState({ type: 'page', status: false });
      });
  }

  public ngOnDestroy(): void {
    this.events.forEach((sub) => sub.unsubscribe());
  }

  public setupLoadingStates(): void {
    this.loadingStates = { page: true, learnings: true };
    // don't add experiences or achievements if they aren't to be shown
    if (this.showExperiences) {
      this.loadingStates.experiences = true;
    }
    if (this.showAchievements) {
      this.loadingStates.achievements = true;
    }
  }

  public updateLoadingState({
    type,
    status,
  }: {
    type: string;
    status: boolean;
  }) {
    // Collect loading states from sub-components and this page, isLoading is changed to true when at least one of the
    // states is true and changed to false when all states have changed to false
    this.loadingStates[type] = status;
    this.isLoading = !!Object.values(this.loadingStates).find((v) => !!v);
  }

  public updateSkillsCounts(): void {
    const isLoggedIn = !!this.authUser;

    forkJoin({
      collectionCounts: this.profileOverviewService.getCollectionCounts(
        this.ownerKey,
        isLoggedIn
      ),
      skillsAndRatingsCount: this.profileOverviewService.getUserSkills(
        this.ownerKey
      ),
      signalCounts: this.profileOverviewService.getSignalCounts(this.ownerKey),
    })
      .pipe(
        take(1),
        switchMap(
          ({
            collectionCounts,
            skillsAndRatingsCount,
            signalCounts,
          }): Observable<SignalSummary[]> => {
            this.collectionCounts = collectionCounts;
            const { skills, ratingsTotalCount } = skillsAndRatingsCount;
            this.skillRatingsCount = ratingsTotalCount;
            this.setSkills(skills);
            this.signalCounts = signalCounts.signalCounts;
            this.signalHistories = signalCounts.signalHistories;
            this.xAxisLabels = signalCounts.xAxisLabels;

            const allTagIds = skills.other.slice(0, 6).concat(skills.focus);

            // get the unique tag ids from the focus skills and the top 6 other skills so that we only get the signal
            // summaries for these tags
            const uniqueTagIds = allTagIds
              .reduce((all, current) => {
                if (!all.includes(current.tagId)) {
                  all.push(current.tagId);
                }
                return all;
              }, [])
              .join(',');

            return this.profileOverviewService.getSignalBreakdown(
              this.ownerKey,
              uniqueTagIds
            );
          }
        )
      )
      .subscribe((signalSummary) => {
        this.signalSummaries = signalSummary.reduce((all, current) => {
          all[current.tagId] = current;
          return all;
        }, {});
        this.updateLoadingState({ type: 'page', status: false });
      });
  }

  public setSkills(skills: TagsApi.FocusableTagDetails): void {
    if (this.hasSkills(skills)) {
      this.skills = skills;
      this.hasNoSkills = !skills?.focus.length && !skills?.other.length;
    } else {
      this.hasNoSkills = true;
      this.forceLoadingStatus(false);
    }
  }

  public goToWizard(): void {
    this.router.navigateByUrl('/getstarted/welcome');
  }

  private forceLoadingStatus(status: boolean): void {
    for (const key of Object.keys(this.loadingStates)) {
      this.loadingStates[key] = status;
    }
    this.isLoading = status;
  }

  private hasSkills(skills: TagsApi.FocusableTagDetails): boolean {
    return skills?.focus?.length > 0 || skills?.other?.length > 0;
  }

  private getPersonalSettings(): Observable<any> {
    // if we're logged out, just returned an empty LocaleId so we'll default to en-US
    return this.authUser
      ? this.userStaticSettingsService.getPersonalSettings()
      : of({ LocaleId: {} });
  }

  private trackProfileView(): void {
    const profileOwnerId = this.ownerIsViewing ? 'self' : this.owner.id;
    const privacyOptions = { 0: 'Public', 1: 'OrgVisible', 2: 'Private' };
    const isFollowing = this.ownerIsViewing
      ? 'self'
      : this.followingService.isViewersFollowee(this.owner.guid);
    // @TODO: Update to new angular when it's ready - PD-52198
    const isEngaged = this.engagedLearnerService.isEnabled && this.isEngaged;
    const trackProperties = {
      ProfileOwnerId: profileOwnerId,
      ProfileVisibility: privacyOptions[this.privacyId],
      SkillsAddedCount: this.skills.other.length,
      SkillRatingsCount: this.skillRatingsCount,
      FocusSkillsCount: this.skills.focus.length,
      ExperiencesCount: this.collectionCounts.experiencesCount,
      AchievementsCount: this.collectionCounts.achievementsCount,
      LearningsCount: this.collectionCounts.learningsCount,
      IsActiveLearner: isEngaged,
      IsFollowing: isFollowing,
      ProfileType: this.profileService.getProfileType(),
    };

    this.trackEvent('Profile Viewed', trackProperties);
  }

  private trackEvent(
    action: string,
    properties?: { [name: string]: any }
  ): void {
    let data: TrackingEventArgs = { action };
    // add properties, if defined
    if (properties) {
      data = {
        ...data,
        properties,
      };
    }
    // track event data
    this.tracker.trackEventData(data);
  }
}
